import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { monitoringExportService } from '@/api/domains/attendance/monitoring';
import type { LC_MonitoringFiltersIn } from '@/api/domains/attendance/monitoring';
import { useHybridExport } from '@/hooks/ui/useHybridExport';
import type { UIExportFormat } from '@/types/fileExport.types';


export interface UseMonitoringExportResult {
  /** `true` mientras la descarga está en curso. */
  isExporting: boolean;
  /** Dispara la descarga del monitoreo con los filtros aplicados. */
  exportMonitoring: (format: UIExportFormat) => Promise<void>;
}

/**
 * Descarga a Excel del monitoreo de asistencia con los filtros aplicados. Delega el branching
 * sync/202, el estado `isExporting` y las notificaciones en `useHybridExport`.
 */
export function useMonitoringExport(
  appliedFilters: LC_MonitoringFiltersIn | null,
): UseMonitoringExportResult {
  const { t: translate } = useTranslation();

  const requestExport = useCallback(
    () => monitoringExportService.export(appliedFilters ?? {}),
    [appliedFilters],
  );

  const { isExporting, runExport } = useHybridExport({
    requestExport,
    messages: {
      error: translate('attendance:monitoring.export.error'),
      success: translate('attendance:monitoring.export.success'),
      queued: translate('attendance:monitoring.export.queued'),
    },
  });

  return { isExporting, exportMonitoring: runExport };
}

export default useMonitoringExport;
